import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Badge,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import {
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  Computer as ComputerIcon,
  Smartphone as SmartphoneIcon,
  Tablet as TabletIcon,
  TrendingUp as TrendingUpIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useErrorSetter } from '../hooks/useUiMessages';
import { fetchRealtimeAnalytics } from './analyticsApi';
import styles from './Analytics.module.css';

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe', '#00c49f', '#8dd1e1', '#d084d0'];

const RealtimeMonitor = () => {
  const setError = useErrorSetter();
  const [loading, setLoading] = useState(true);
  const [realtimeData, setRealtimeData] = useState({
    activeUsers: 0,
    currentSessions: 0,
    pageViews: 0,
    averageLoadTime: 0,
    liveUsers: [],
    recentActivity: [],
    trafficTrend: [],
    topPages: [],
  deviceBreakdown: [],
  topCountries: [],
    errorEvents: [],
    systemHealth: {
      cpuUsage: 0,
      memoryUsage: 0,
      responseTime: 0,
      errorRate: 0
    }
  });

  // Simulated WebSocket connection for live updates
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const fetchRealtimeData = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('afd8TvhsdjwiuuvsgjhsAfgsUhjs');
      if (!token) {
        setRealtimeData(prev => ({ ...prev, activeUsers:0, currentSessions:0, pageViews:0 }));
        setLastUpdate(new Date());
        return;
      }
  const raw = await fetchRealtimeAnalytics();
      const system = raw.systemHealth || {};
  const shortWindow = raw.shortWindow || {};
  const countries = raw.topCountries || [];
  const devices = raw.deviceBreakdown || [];
      const minuteBreakdown = raw.minuteBreakdown || [];
      const totalCountryEvents = countries.reduce((s,c)=> s + (c.count||0), 0) || 1;
      const topCountries = countries.map(c => ({ country: c._id || c.country || 'Unknown', count: c.count || 0, percentage: +(((c.count||0)/totalCountryEvents)*100).toFixed(1) }));
  const totalDeviceEvents = devices.reduce((s,d)=> s + (d.count||0), 0) || 1;
  const deviceBreakdown = devices.map(d => ({ device: (d.device || '').toLowerCase() || 'unknown', count: d.count || 0, percentage: +(((d.count||0)/ totalDeviceEvents)*100).toFixed(1) }));
    setRealtimeData({
        activeUsers: shortWindow.uniqueUsers || 0,
        currentSessions: shortWindow.uniqueSessions || 0,
        pageViews: raw.pageViews1h || 0,
        averageLoadTime: raw.avgLoadTimeMs ? +(raw.avgLoadTimeMs/1000).toFixed(1) : 0,
        liveUsers: raw.liveUsers || [],
        recentActivity: (raw.recentEvents || []).slice(0,15).map((e,i)=>({ id:i+1, event: e.eventType, page: e.product?.featureUsed || '-', time: new Date(e.timestamp).toLocaleTimeString(), user: e.userType })),
        trafficTrend: minuteBreakdown,
        topPages: raw.topPages || [],
  deviceBreakdown,
        topCountries,
        errorEvents: [],
        systemHealth: {
      cpuUsage: typeof system.cpuUsage === 'number' ? system.cpuUsage : 0,
      memoryUsage: typeof system.memoryUsage === 'number' ? system.memoryUsage : 0,
          responseTime: typeof system.responseTime === 'number' ? system.responseTime : 0,
          errorRate: shortWindow.errors ? +( (shortWindow.errors / (shortWindow.total || 1))*100 ).toFixed(1) : 0,
          eventsPerMinute: shortWindow.eventsPerMinute || 0
        }
      });
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Error fetching real-time data:', err);
      setError('Failed to load realtime metrics');
    } finally {
      setLoading(false);
    }
  }, [setError]);

  // Removed mock generators – now relying on backend realtime endpoint.

  const getDeviceIcon = (device) => {
    switch (device.toLowerCase()) {
      case 'mobile': return <SmartphoneIcon fontSize="small" />;
      case 'tablet': return <TabletIcon fontSize="small" />;
      default: return <ComputerIcon fontSize="small" />;
    }
  };

  const getHealthColor = (value, type) => {
    switch (type) {
      case 'cpu':
      case 'memory':
        if (value < 50) return 'success';
        if (value < 80) return 'warning';
        return 'error';
      case 'response':
        if (value < 200) return 'success';
        if (value < 500) return 'warning';
        return 'error';
      case 'error':
        if (value < 1) return 'success';
        if (value < 3) return 'warning';
        return 'error';
      default:
        return 'primary';
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Set up interval for live updates
  useEffect(() => {
    fetchRealtimeData();
    
    const interval = setInterval(() => {
      fetchRealtimeData();
  }, 20000); // Update every 20 seconds

    return () => clearInterval(interval);
  }, [fetchRealtimeData]);

  if (loading) {
    return (
      <Box className={styles.loadingContainer}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading real-time data...
        </Typography>
      </Box>
    );
  }

  return (
    <div className={styles.realtimeContainer}>
      {/* Real-time Status Bar */}
      <Alert 
        severity="success" 
        icon={<SuccessIcon />}
        className={styles.statusAlert}
      >
        <Typography variant="body2">
          Live monitoring active • Last updated: {format(lastUpdate, 'HH:mm:ss')} • 
          Auto-refresh every 20 seconds
        </Typography>
      </Alert>

      {/* Current Metrics */}
      <Grid container spacing={3} className={styles.metricsGrid}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={styles.realtimeMetricCard}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <Badge badgeContent="LIVE" color="error">
                  <VisibilityIcon color="primary" sx={{ mr: 1 }} />
                </Badge>
                <Typography variant="h6" color="textSecondary">
                  Active Users
                </Typography>
              </Box>
              <Typography variant="h3" className={styles.metricValue}>
                {realtimeData.activeUsers}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className={styles.realtimeMetricCard}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <PersonIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6" color="textSecondary">
                  Current Sessions
                </Typography>
              </Box>
              <Typography variant="h3" className={styles.metricValue}>
                {realtimeData.currentSessions}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className={styles.realtimeMetricCard}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <TrendingUpIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="h6" color="textSecondary">
                  Page Views (1h)
                </Typography>
              </Box>
              <Typography variant="h3" className={styles.metricValue}>
                {formatNumber(realtimeData.pageViews)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className={styles.realtimeMetricCard}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <SpeedIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6" color="textSecondary">
                  Avg Load Time
                </Typography>
              </Box>
              <Typography variant="h3" className={styles.metricValue}>
                {realtimeData.averageLoadTime}s
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts and Activity */}
      <Grid container spacing={3} className={styles.chartsGrid}>
        {/* Live Traffic Trend */}
        <Grid item xs={12} lg={8}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Live Traffic (Last 30 minutes)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={realtimeData.trafficTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="users" 
                    stroke={COLORS[0]} 
                    strokeWidth={2}
                    name="Active Users"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="pageViews" 
                    stroke={COLORS[1]} 
                    strokeWidth={2}
                    name="Page Views"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="sessions" 
                    stroke={COLORS[2]} 
                    strokeWidth={2}
                    name="Sessions"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* System Health */}
        <Grid item xs={12} lg={4}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                System Health
              </Typography>
              <Box className={styles.healthMetrics}>
                <Box className={styles.healthMetric}>
                  <Typography variant="body2" color="textSecondary">
                    CPU Usage
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={realtimeData.systemHealth.cpuUsage}
                    color={getHealthColor(realtimeData.systemHealth.cpuUsage, 'cpu')}
                    sx={{ mt: 1, mb: 1 }}
                  />
                  <Typography variant="body2">
                    {realtimeData.systemHealth.cpuUsage}%
                  </Typography>
                </Box>
                
                <Box className={styles.healthMetric}>
                  <Typography variant="body2" color="textSecondary">
                    Memory Usage
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={realtimeData.systemHealth.memoryUsage}
                    color={getHealthColor(realtimeData.systemHealth.memoryUsage, 'memory')}
                    sx={{ mt: 1, mb: 1 }}
                  />
                  <Typography variant="body2">
                    {realtimeData.systemHealth.memoryUsage}%
                  </Typography>
                </Box>
                
                <Box className={styles.healthMetric}>
                  <Typography variant="body2" color="textSecondary">
                    Response Time
                  </Typography>
                  <Chip 
                    label={`${realtimeData.systemHealth.responseTime}ms`}
                    color={getHealthColor(realtimeData.systemHealth.responseTime, 'response')}
                    size="small"
                  />
                </Box>
                
                <Box className={styles.healthMetric}>
                  <Typography variant="body2" color="textSecondary">
                    Error Rate
                  </Typography>
                  <Chip 
                    label={`${realtimeData.systemHealth.errorRate}%`}
                    color={getHealthColor(realtimeData.systemHealth.errorRate, 'error')}
                    size="small"
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Live Users */}
        <Grid item xs={12} md={6}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Active Users Right Now
              </Typography>
              <List className={styles.liveUsersList}>
                {realtimeData.liveUsers.map((user) => (
                  <ListItem key={user.id} className={styles.liveUserItem}>
                    <ListItemAvatar>
                      <Avatar className={styles.userAvatar}>
                        {user.flag}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          <Typography variant="body2">
                            {user.country}
                          </Typography>
                          <Chip 
                            icon={getDeviceIcon(user.device)}
                            label={user.device}
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="textSecondary">
                          {user.page} • {Math.floor(user.duration / 60)}m {user.duration % 60}s
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Recent Activity
              </Typography>
              <TableContainer component={Paper} className={styles.activityTable}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Event</TableCell>
                      <TableCell>Page</TableCell>
                      <TableCell>User</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {realtimeData.recentActivity.map((activity) => (
                      <TableRow key={activity.id}>
                        <TableCell>
                          <Chip 
                            label={activity.time}
                            size="small"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>{activity.event}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {activity.page}
                          </Typography>
                        </TableCell>
                        <TableCell>{activity.user}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Pages */}
        <Grid item xs={12} md={6}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Top Pages (Live)
              </Typography>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={realtimeData.topPages}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="page" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="views" fill={COLORS[3]} name="Views" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Countries */}
        <Grid item xs={12} md={6}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Top Countries (Live)
              </Typography>
              <Box className={styles.deviceBreakdown}>
                {realtimeData.topCountries.map((c) => (
                  <Box key={c.country} className={styles.deviceItem}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Typography variant="body1">
                        {c.country}
                      </Typography>
                      <Chip 
                        label={`${c.count} (${c.percentage}%)`}
                        size="small"
                        sx={{ ml: 'auto' }}
                      />
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={c.percentage}
                      color="primary"
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Devices */}
        <Grid item xs={12} md={6}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Top Devices (Live)
              </Typography>
              <Box className={styles.deviceBreakdown}>
                {realtimeData.deviceBreakdown.map((d) => (
                  <Box key={d.device} className={styles.deviceItem}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getDeviceIcon(d.device)} {d.device}
                      </Typography>
                      <Chip 
                        label={`${d.count} (${d.percentage}%)`}
                        size="small"
                        sx={{ ml: 'auto' }}
                      />
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={d.percentage}
                      color="secondary"
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Events */}
      {realtimeData.errorEvents.length > 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert severity="warning" icon={<WarningIcon />}>
              <Typography variant="h6">Recent Error Events</Typography>
              <Box sx={{ mt: 1 }}>
                {realtimeData.errorEvents.map((error) => (
                  <Chip
                    key={error.id}
                    icon={<ErrorIcon />}
                    label={`${error.type} (${error.count}) - Last: ${error.lastOccurred}`}
                    color="warning"
                    variant="outlined"
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </Box>
            </Alert>
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default RealtimeMonitor;
